import React from 'react';
import {
  getLabelTextAnchor,
  getLabelX,
  getLabelY,
  getScaleRadialX,
  getScaleRadialY,
  indexToRadians,
} from './utils';
import { AXIS_LABEL_HEIGHT, LABEL_MAX_LENGTH, LEGEND_X_OFFSET } from './consts';
import { colors } from '@ardoq/design-tokens';
import { ChartConfiguration } from './types';
import { WithPopover } from '@ardoq/popovers';
import { returnZero } from '@ardoq/common-helpers';
import { ComponentLabelParts, truncateComponentLabel } from '@ardoq/graph';
import styled from 'styled-components';
import { spiderChartCommands } from '../commands';
import { ArdoqId } from '@ardoq/api-types';
import {
  type LabelState,
  DEFAULT_FONT_SIZE,
  measureLabel,
} from './labelVisibility';

const TEXT_OFFSET = 20;

const AxisLabel = styled.text<{ $isUnfocused: boolean }>`
  cursor: pointer;
  opacity: ${props => (props.$isUnfocused ? 0.32 : 1)};
`;

type ChartAxisProps = {
  config: ChartConfiguration;
  index: number;
  componentId: ArdoqId;
  labelInfo: ComponentLabelParts;
  isUnfocused: boolean;
  labelState: LabelState;
};

export const ChartAxis = ({
  index,
  componentId,
  labelInfo,
  config,
  isUnfocused,
  labelState,
}: ChartAxisProps) => {
  const {
    total,
    radius,
    centerX,
    centerY,
    legendHeight,
    width,
    extraWidth,
    labelFontSize,
  } = config;
  const widthScale = labelFontSize / DEFAULT_FONT_SIZE;

  const x = getLabelX(centerX, radius, index, total);
  const y = getLabelY(centerY, radius, index, total);
  const textAnchor = getLabelTextAnchor(index, total);
  const adaptiveAxisLabelHeight = Math.round(
    AXIS_LABEL_HEIGHT * (labelFontSize / DEFAULT_FONT_SIZE)
  );
  const isBelowLegend = y + adaptiveAxisLabelHeight / 2 > legendHeight;
  const availableWidth =
    textAnchor === 'start'
      ? isBelowLegend
        ? width + extraWidth / 2 - x - TEXT_OFFSET // the label is on the right side, clear of the legend. available width is from x to the viewport right.
        : width - LEGEND_X_OFFSET - x - TEXT_OFFSET // the label is on the right side, but not clear of the legend. available width is from x to the legend left.
      : textAnchor === 'end'
        ? x + extraWidth / 2 - TEXT_OFFSET // the label is on the left side. available width is from x to the left side of the viewport.
        : (width - LEGEND_X_OFFSET - x) * 2 - TEXT_OFFSET; // the label is centered. available width is up to the legend left-- but for a centered label, there is space on the left too. so we use the formula for right-side labels and double it.

  const fullLabel = truncateComponentLabel({
    ...labelInfo,
    measure: returnZero,
    width: Infinity,
  });

  // measureLabel uses DEFAULT_FONT_SIZE; scale availableWidth up by the inverse
  // ratio so truncation treats the budget as if measured at labelFontSize.
  const scaledAvailableWidth = availableWidth / widthScale;
  const maxWidth =
    fullLabel.length > LABEL_MAX_LENGTH
      ? Math.min(
          scaledAvailableWidth,
          measureLabel(`${fullLabel.slice(0, LABEL_MAX_LENGTH)}...`)
        )
      : scaledAvailableWidth;

  const label = truncateComponentLabel({
    ...labelInfo,
    measure: measureLabel,
    width: maxWidth,
  });

  const isLabelTruncated = label !== fullLabel;
  const textProps = {
    width: maxWidth,
    x,
    y,
    textAnchor,
  };

  const handleAxisClick = (e: React.MouseEvent<SVGTextElement, MouseEvent>) => {
    e.stopPropagation();
    spiderChartCommands.setFocusedItemState(componentId);
  };

  return (
    <g key={fullLabel} className="axis">
      <line
        x1={centerX}
        y1={centerY}
        x2={getScaleRadialX(centerX, 1, radius, indexToRadians(index, total))}
        y2={getScaleRadialY(centerY, 1, radius, indexToRadians(index, total))}
        className="line"
        stroke={colors.grey400}
        opacity={isUnfocused ? 0.32 : 1}
        strokeWidth="1px"
      />
      {labelState === 'ellipsis' ? (
        <WithPopover content={fullLabel}>
          <AxisLabel
            $isUnfocused={isUnfocused}
            onClick={handleAxisClick}
            x={x}
            y={y}
            textAnchor={textAnchor}
            fontSize={labelFontSize}
          >
            {'…'}
          </AxisLabel>
        </WithPopover>
      ) : isLabelTruncated ? (
        <WithPopover content={fullLabel}>
          <AxisLabel
            $isUnfocused={isUnfocused}
            onClick={handleAxisClick}
            {...textProps}
            fontSize={labelFontSize}
          >
            {label}
          </AxisLabel>
        </WithPopover>
      ) : (
        <AxisLabel
          $isUnfocused={isUnfocused}
          onClick={handleAxisClick}
          {...textProps}
          fontSize={labelFontSize}
        >
          {fullLabel}
        </AxisLabel>
      )}
    </g>
  );
};
